import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Truck, Fuel, Camera, FileText, Wallet, Wrench, AlertTriangle,
  Download, RefreshCcw, Plus, Clock, MessageSquare, FileBadge, ClipboardCheck, CircleDollarSign,
  UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Report {
  site: { id: string; code: string | null; name: string | null };
  reportDate: string;
  generatedAt: string;
  flag: 'green' | 'yellow' | 'red';
  healthScore: number;
  summary: {
    trips: { count: number; deliveredQty: number; revenueSar: number; planned: number | null };
    fuel: { txCount: number; liters: number; sarSpent: number };
    cash: { closingCount: number; expectedSar: number; actualSar: number; varianceSar: number; flagged: boolean };
    photos: { onTime: number; late: number; total: number };
    vouchers: { count: number };
    expenses: { count: number; totalSar: number };
    maintenance: { count: number; totalCostSar: number };
    anomalies: { open: number; critical: number; high: number };
    idleWaste: { wastedMin: number; costSar: number };
    documents: { renewalsCount: number };
  };
  variances: { label: string; expected: string | null; actual: string; variance: string | null; status: 'ok' | 'watch' | 'bad' }[];
  trips: { id: string; code: string | null; status: string; driverId: string | null; driverName: string | null;
            vehicleId: string | null; vehiclePlate: string | null; deliveredQty: number; unit: string;
            clientPayment: number; hasPhoto: boolean; hasVoucher: boolean }[];
  activityLog: { id: string; entityType: string; entityId: string; occurredAt: string; submittedAt: string;
                 isLate: boolean; daysLate: number; description: string; submittedByName: string | null }[];
  notes: { id: string; note: string; addedByName: string | null; addedAt: string }[];
  comparison: { yesterday: { trips: number; revenueSar: number; fuelSar: number } | null;
                 sevenDayAvg: { trips: number; revenueSar: number; fuelSar: number } | null };
  lateAdditions: { entityType: string; description: string; daysLate: number; submittedAt: string }[];
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ICON_FOR: Record<string, React.ElementType> = {
  trip: Truck, fuel: Fuel, photo: Camera, voucher: FileText, expense: Wallet,
  cash: CircleDollarSign, maintenance: Wrench, attendance: UserCheck,
  document: FileBadge, coaching: MessageSquare, anomaly: AlertTriangle,
  closing: ClipboardCheck,
};

const FLAG_TONE: Record<string, string> = {
  green: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40',
  yellow: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40',
  red: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/40',
};

type Tab = 'overview' | 'activity' | 'trips' | 'fuel' | 'anomalies' | 'notes';

export default function DailySiteReportPage() {
  const { siteId, date } = useParams<{ siteId: string; date: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const refresh = useCallback(async () => {
    if (!siteId || !date) return;
    try {
      const r = await apiClient.get<Report>('/api/v1/daily-reports/site/' + siteId + '/date/' + date);
      setReport(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [siteId, date]);

  useEffect(() => { refresh(); }, [refresh]);

  async function regenerate() {
    if (!siteId || !date) return;
    setRefreshing(true);
    try {
      await apiClient.post('/api/v1/daily-reports/site/' + siteId + '/date/' + date + '/regenerate');
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }

  async function downloadPdf() {
    if (!siteId || !date || !report) return;
    const jwt = getStoredToken();
    const res = await fetch(API_BASE + '/api/v1/daily-reports/site/' + siteId + '/date/' + date + '/pdf', {
      headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
    });
    if (!res.ok) { window.alert('PDF failed: ' + res.statusText); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'daily_' + (report.site.code ?? 'site') + '_' + date + '.pdf';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function addNote() {
    if (!siteId || !date || !noteText.trim()) return;
    setSavingNote(true);
    try {
      await apiClient.post('/api/v1/daily-reports/site/' + siteId + '/date/' + date + '/note', { note: noteText.trim() });
      setNoteText('');
      await refresh();
    } finally {
      setSavingNote(false);
    }
  }

  function jumpDate(offsetDays: number) {
    if (!date || !siteId) return;
    const d = new Date(date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + offsetDays);
    navigate('/dashboard/reports/sites/' + siteId + '/' + d.toISOString().slice(0, 10));
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !report) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground">{error ?? 'No data'}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
          <Link to="/dashboard/reports">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Reports</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2">
            {report.site.name ?? '—'}
            <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ' + FLAG_TONE[report.flag]}>
              {report.flag} · {report.healthScore}/100
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => jumpDate(-1)}>‹</Button>
            <Input type="date" value={date} onChange={(e) => navigate('/dashboard/reports/sites/' + siteId + '/' + e.target.value)} className="h-8 w-36" />
            <Button variant="ghost" size="sm" onClick={() => jumpDate(1)}>›</Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={regenerate} disabled={refreshing} className="gap-2 h-8">
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              Regen
            </Button>
            <Button size="sm" onClick={downloadPdf} className="gap-2 h-8">
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Stat label="Trips" value={String(report.summary.trips.count)} sub={report.comparison.yesterday ? 'y: ' + report.comparison.yesterday.trips : ''} />
          <Stat label="Delivered" value={report.summary.trips.deliveredQty.toFixed(1)} />
          <Stat label="Revenue" value={fmtSar(report.summary.trips.revenueSar)} />
          <Stat label="Fuel" value={fmtSar(report.summary.fuel.sarSpent)} sub={report.summary.fuel.liters.toFixed(0) + ' L'} />
          <Stat label="Cash variance" value={fmtSar(report.summary.cash.varianceSar)} sub={report.summary.cash.flagged ? 'flagged' : 'ok'} tone={report.summary.cash.flagged ? 'bad' : undefined} />
          <Stat label="Idle waste" value={fmtSar(report.summary.idleWaste.costSar)} sub={Math.round(report.summary.idleWaste.wastedMin) + ' min'} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {([
            { v: 'overview', l: 'Overview' },
            { v: 'activity', l: 'Activity log (' + report.activityLog.length + ')' },
            { v: 'trips', l: 'Trips (' + report.trips.length + ')' },
            { v: 'fuel', l: 'Fuel (' + report.summary.fuel.txCount + ')' },
            { v: 'anomalies', l: 'Anomalies (' + report.summary.anomalies.open + ')' },
            { v: 'notes', l: 'Notes (' + report.notes.length + ')' },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-3 py-2 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border">
              <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">Variance reconciliation</div>
              {report.variances.map((v, i) => (
                <div key={i} className="px-4 py-2 border-t border-border first:border-t-0 flex items-baseline justify-between gap-2 text-sm">
                  <span className="font-medium">{v.label}</span>
                  <span className="text-xs text-muted-foreground font-mono">expected {v.expected ?? '—'} · actual {v.actual} · {v.variance ?? '—'}</span>
                  <span className={'text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (v.status === 'ok' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : v.status === 'watch' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400' : 'bg-rose-500/15 text-rose-700 dark:text-rose-400')}>{v.status}</span>
                </div>
              ))}
            </div>

            {report.lateAdditions.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
                <div className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2 inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> Late additions</div>
                <div className="space-y-1">
                  {report.lateAdditions.map((l, i) => (
                    <div key={i} className="text-xs">
                      <span className={l.daysLate > 3 ? 'text-rose-700 dark:text-rose-400' : 'text-amber-700 dark:text-amber-400'}>+{l.daysLate}d</span>
                      <span className="text-muted-foreground"> · {l.entityType} · </span>
                      <span>{l.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'activity' && (
          report.activityLog.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No activity logged for this day.</div>
          ) : (
            <div className="rounded-lg border border-border">
              {report.activityLog.map((a) => {
                const Icon = ICON_FOR[a.entityType] ?? Truck;
                const t = new Date(a.occurredAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={a.id} className="px-4 py-2 border-t border-border first:border-t-0 flex items-baseline gap-3 text-sm">
                    <span className="font-mono text-xs text-muted-foreground w-12">{t}</span>
                    <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1">{a.description}</span>
                    {a.submittedByName && <span className="text-xs text-muted-foreground">by {a.submittedByName}</span>}
                    {a.daysLate > 0 && <span className="text-xs text-amber-600 dark:text-amber-400">added {a.daysLate} day{a.daysLate > 1 ? 's' : ''} late</span>}
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === 'trips' && (
          report.trips.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No trips for this day.</div>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Driver</th>
                    <th className="text-left px-3 py-2">Vehicle</th>
                    <th className="text-right px-3 py-2">Delivered</th>
                    <th className="text-right px-3 py-2">Pay</th>
                    <th className="text-center px-3 py-2">Photo</th>
                    <th className="text-center px-3 py-2">Voucher</th>
                  </tr>
                </thead>
                <tbody>
                  {report.trips.map((t) => (
                    <tr key={t.id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                      <td className="px-3 py-2 font-mono text-xs">{t.code}</td>
                      <td className="px-3 py-2 text-xs">{t.status}</td>
                      <td className="px-3 py-2">{t.driverName ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{t.vehiclePlate ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{t.deliveredQty.toFixed(1)} {t.unit}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtSar(t.clientPayment)}</td>
                      <td className="px-3 py-2 text-center">{t.hasPhoto ? '✅' : '⚠'}</td>
                      <td className="px-3 py-2 text-center">{t.hasVoucher ? '✅' : '🔴'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'fuel' && (
          <div className="rounded-lg border border-border p-4 text-sm">
            <div className="flex justify-between mb-2"><span>Transactions</span><span className="font-mono">{report.summary.fuel.txCount}</span></div>
            <div className="flex justify-between mb-2"><span>Liters</span><span className="font-mono">{report.summary.fuel.liters.toFixed(1)} L</span></div>
            <div className="flex justify-between"><span>SAR</span><span className="font-mono">{fmtSar(report.summary.fuel.sarSpent)}</span></div>
            {report.activityLog.filter((a) => a.entityType === 'fuel').length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Per transaction</div>
                {report.activityLog.filter((a) => a.entityType === 'fuel').map((a) => (
                  <div key={a.id} className="text-xs py-1 border-t border-border first:border-t-0 first:pt-0">{a.description}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'anomalies' && (
          report.summary.anomalies.open === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No open anomalies for this day.</div>
          ) : (
            <div className="rounded-lg border border-border">
              {report.activityLog.filter((a) => a.entityType === 'anomaly').map((a) => (
                <div key={a.id} className="px-4 py-2 border-t border-border first:border-t-0 text-sm">
                  <div className="flex items-baseline justify-between"><span>{a.description}</span></div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'notes' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3 flex items-end gap-2">
              <div className="flex-1">
                <textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note (e.g. 'Driver Ali was sick today')"
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <Button onClick={addNote} disabled={savingNote || !noteText.trim()} className="gap-2">
                {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
              </Button>
            </div>
            {report.notes.length === 0 ? (
              <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No notes yet.</div>
            ) : (
              <div className="space-y-2">
                {report.notes.map((n) => (
                  <div key={n.id} className="rounded-lg border border-border p-3 text-sm">
                    <div className="text-xs text-muted-foreground mb-1">{new Date(n.addedAt).toLocaleString('en-GB')} · {n.addedByName ?? 'admin'}</div>
                    <div className="whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'bad' | 'good' }) {
  const cls = tone === 'bad' ? 'text-rose-600 dark:text-rose-400' : tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : '';
  return (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-lg font-medium mt-0.5 ' + cls}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
